import React, { lazy, Suspense } from 'react';
import { Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { AuthProvider, useAuth } from './components/contexts/AuthContext';
import LoadingIndicator from './components/common/LoadingIndicator';
import './App.css';

// Layout components
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import ScrollToTop from './components/common/ScrollToTop';
import CookieConsent from './components/CookieConsent';

// Auth components
import SignUp from './components/auth/SignUp';
import Login from './components/auth/Login';
import PrivateRoute from './components/auth/PrivateRoute';
import BlogPage from './components/blog/BlogPage';
import BlogPost from './components/blog/BlogPost';
import AdminRoute from './components/auth/AdminRoute';
import RoleBasedRoute from './components/auth/RoleBasedRoute';
import EmailVerification from './components/auth/EmailVerification';
import ForgotPassword from './components/auth/ForgotPassword';

// Admin components
import AdminDashboard from './components/admin/AdminDashboard';
import BlogManagement from './components/admin/BlogManagement';
import ContactManagement from './components/admin/ContactManagement';
import DonationManagement from './components/admin/DonationManagement';
import ScholarshipManagement from './components/admin/ScholarshipManagement';
import ScholarshipApplicationsManagement from './components/admin/ScholarshipApplicationsManagement';
import UserManagement from './components/admin/UserManagement';
import CourseManagement from './components/admin/CourseManagement';
import QuizManagement from './components/admin/QuizManagement';
import ResearchManagement from './components/admin/ResearchManagement';
import CareerManagement from './components/admin/CareerManagement';
import Analytics from './components/admin/Analytics';
import CertificatesManagement from './components/admin/CertificatesManagement';
import AnnouncementsManager from './components/admin/AnnouncementsManager';
import Reports from './components/admin/Reports';
import ScholarshipReviews from './components/admin/ScholarshipReviews';
import NotificationManagement from './components/admin/NotificationManagement';
import EventManagement from './components/admin/EventManagement';

// Landing components
import LandingPage from './components/landing/LandingPage';
import EventsPage from './components/events/EventsPage';
import EventDetails from './components/events/EventDetails';

// Dashboard components
import Dashboard from './components/dashboard/UserDashboard';

// Course components
import CoursePage from './components/courses/CoursePage';
import CourseContent from './components/courses/CourseContent';
import CoursePresentation from './components/courses/CoursePresentation';
import Certificate from './components/courses/Certificate';
import VerifyCertificate from './components/courses/VerifyCertificate';

// Exam components
import ExamPage from './components/courses/ExamPage';
import ResultsPage from './components/courses/ResultsPage';

// Quiz components
import Quiz from './components/quiz/Quiz';
import PublicQuizEntry from './components/quiz/PublicQuizEntry';
import QuizResults from './components/quiz/QuizResults';
import QuizRepository from './components/quiz/QuizRepository';
import QuizBattleTake from './components/quiz/QuizBattleTake';
import QuizBattleResults from './components/quiz/QuizBattleResults';

// Scholarship components
import ScholarshipPage from './components/scholarships/ScholarshipPage';
import ScholarshipApplication from './components/scholarships/ScholarshipApplication';
import MyApplications from './components/scholarships/MyApplications';

// Common components
import NotFound from './components/common/NotFound';
import ApplicationSuccess from './components/scholarships/ApplicationSuccess';

// Page components
import Donate from './components/pages/Donate';
import About from './components/pages/About';
import FAQ from './components/pages/FAQ';
import Support from './components/pages/Support';
import Terms from './components/pages/Terms';
import Privacy from './components/pages/Privacy';
import Cookies from './components/pages/Cookies';
import Research from './components/pages/Research';
import Careers from './components/pages/Careers';
import ResearchSupportLayout from './components/research-support/ResearchSupportLayout';
import Overview from './components/research-support/Overview';
import ApplicationPortal from './components/research-support/ApplicationPortal';
import Guidelines from './components/research-support/Guidelines';
import ResearchSupportFAQ from './components/research-support/FAQ';
import Reviewers from './components/research-support/Reviewers';
import ContactSupport from './components/research-support/ContactSupport';
import MySubmissions from './components/research-support/MySubmissions';
import SubmissionSuccess from './components/research-support/SubmissionSuccess';
import ResearchSupportManagement from './components/admin/ResearchSupportManagement';
import CognitionLabManagement from './components/admin/CognitionLabManagement';

// Cognition Lab components (lazy loaded)
const CognitionLabDashboard = lazy(() => import('./components/cognition-lab/CognitionLabDashboard'));
const TypingSpeedTest = lazy(() => import('./components/cognition-lab/TypingSpeedTest'));
const SpeedReadingTest = lazy(() => import('./components/cognition-lab/SpeedReadingTest'));
const MemoryRecallTest = lazy(() => import('./components/cognition-lab/MemoryRecallTest'));
const TypingAccuracyDrill = lazy(() => import('./components/cognition-lab/TypingAccuracyDrill'));
const FlashComprehensionCheck = lazy(() => import('./components/cognition-lab/FlashComprehensionCheck'));
const CognitionLabCertificate = lazy(() => import('./components/cognition-lab/CognitionLabCertificate'));

const loadCognitionLabCertificates = async () => {
  try {
    return await import('./components/cognition-lab/CognitionLabCertificates');
  } catch (error) {
    console.error('Error loading CognitionLabCertificates:', error);
    const isChunkError = error.name === 'ChunkLoadError' || /Loading chunk .* failed/.test(error.message);
    if (isChunkError && !window.__CognitionLabCertificatesReloaded) {
      window.__CognitionLabCertificatesReloaded = true;
      window.location.reload();
      return new Promise(() => {});
    }
    return {
      default: () => (
        <div className="min-h-screen flex items-center justify-center p-8">
          <div className="max-w-xl text-center bg-white rounded-xl shadow-lg border border-gray-200 p-8">
            <h2 className="text-2xl font-bold text-[rgb(130,88,18)] mb-4">Unable to load certificates</h2>
            <p className="text-gray-700 mb-6">There was a problem loading the Cognition Lab certificates page. Please refresh the page or try again later.</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-[rgb(130,88,18)] text-white px-5 py-3 rounded-full"
            >
              Refresh Page
            </button>
          </div>
        </div>
      )
    };
  }
};

const CognitionLabCertificates = lazy(loadCognitionLabCertificates);

// Pattern Recognition components (lazy loaded)
const MatrixPatternTest = lazy(() => import('./components/cognition-lab/pattern-recognition/MatrixPatternTest'));
const ShapeRotationTest = lazy(() => import('./components/cognition-lab/pattern-recognition/ShapeRotationTest'));
const SequencePatternTest = lazy(() => import('./components/cognition-lab/pattern-recognition/SequencePatternTest'));
const MechanicalReasoningTest = lazy(() => import('./components/cognition-lab/pattern-recognition/MechanicalReasoningTest'));
const ClockPuzzleTest = lazy(() => import('./components/cognition-lab/pattern-recognition/ClockPuzzleTest'));
const PatternTestSetup = lazy(() => import('./components/cognition-lab/pattern-recognition/PatternTestSetup'));

// Protected Route wrapper component
const ProtectedRoute = ({ children }) => {
  const { currentUser } = useAuth();
  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }
  return children;
};


function App() {
  const location = useLocation();
  
  // Updated logic for navbar and footer visibility
  const isLandingPage = location.pathname === '/';
  const isAuthPage = ['/login', '/signup'].includes(location.pathname);
  const isAdminPage = location.pathname.startsWith('/admin');
  const showNavbar = !isAuthPage && !isAdminPage;
  const showFooter = !isAuthPage && !isAdminPage;

  return (
    <HelmetProvider>
      <Helmet>
        <title>Intellectual Oasis Fellowship</title>
      </Helmet>
      <AuthProvider>
        <ScrollToTop />
        <div className="App min-h-screen flex flex-col">
          {showNavbar && <Navbar />}
          <main className={`flex-grow ${!isLandingPage ? 'px-4 sm:px-6 lg:px-8 mt-20' : ''}`}>
          <Suspense fallback={<div className="text-center p-10">Loading...</div>}>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/signup" element={<SignUp />} />
              <Route path="/login" element={<Login />} />
              <Route path="/verify-email" element={<EmailVerification />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:postId" element={<BlogPost />} />
              <Route path="/events" element={<EventsPage />} />
              <Route path="/events/:eventId" element={<EventDetails />} />
              <Route path="/scholarships" element={<ScholarshipPage />} />
              <Route path="/public-quiz/:quizId" element={<PublicQuizEntry />} />
              <Route path="/verify" element={<VerifyCertificate />} />
              <Route path="/donate" element={<Donate />} />
              <Route path="/about" element={<About />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/support" element={<Support />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/cookies" element={<Cookies />} />
              <Route path="/research" element={<Research />} />
              <Route path="/research-support" element={<ResearchSupportLayout />}>
                <Route index element={<Overview />} />
                <Route path="apply" element={<PrivateRoute><ApplicationPortal /></PrivateRoute>} />
                <Route path="guidelines" element={<Guidelines />} />
                <Route path="faq" element={<ResearchSupportFAQ />} />
                <Route path="reviewers" element={<Reviewers />} />
                <Route path="contact" element={<ContactSupport />} />
                <Route path="my-submissions" element={<PrivateRoute><MySubmissions /></PrivateRoute>} />
                <Route path="success" element={<PrivateRoute><SubmissionSuccess /></PrivateRoute>} />
              </Route>
              <Route path="/careers" element={<Careers />} />

              {/* Protected Routes */}
              <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
              <Route path="/quiz-repository" element={<PrivateRoute><QuizRepository /></PrivateRoute>} />
              <Route path="/courses" element={<PrivateRoute><CoursePage /></PrivateRoute>} />
              <Route path="/courses/:courseId" element={<PrivateRoute><CourseContent /></PrivateRoute>} />
              <Route path="/courses/:courseId/module/:moduleIndex/subcourse/:subCourseIndex" element={<PrivateRoute><CoursePresentation /></PrivateRoute>} />
              <Route path="/courses/:courseId/exam" element={<PrivateRoute><ExamPage /></PrivateRoute>} />
              <Route path="/courses/:courseId/results" element={<PrivateRoute><ResultsPage /></PrivateRoute>} />
              <Route path="/courses/:courseId/certificate" element={<PrivateRoute><Certificate /></PrivateRoute>} />
              <Route path="/courses/:courseId/content/:moduleTitle/:subCourseId" element={<PrivateRoute><CoursePresentation /></PrivateRoute>} />
              <Route path="/take-quiz/:quizId" element={<PrivateRoute><Quiz /></PrivateRoute>} />
              <Route path="/quiz-results/:quizId" element={<PrivateRoute><QuizResults /></PrivateRoute>} />
              <Route path="/take-battle/:battleId" element={<PrivateRoute><QuizBattleTake /></PrivateRoute>} />
              <Route path="/battle-results/:battleId" element={<PrivateRoute><QuizBattleResults /></PrivateRoute>} />
              <Route path="/scholarships/:scholarshipId/apply" element={<PrivateRoute><ScholarshipApplication /></PrivateRoute>} />
              <Route path="/scholarships/my-applications" element={<PrivateRoute><MyApplications /></PrivateRoute>} />
              <Route path="/scholarships/application-success" element={<PrivateRoute><ApplicationSuccess /></PrivateRoute>} />

              {/* Admin Routes */}
              <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>}>
                <Route index element={<Navigate to="/admin/users" replace />} />
                <Route path="users" element={<RoleBasedRoute requiredRole="users"><UserManagement /></RoleBasedRoute>} />
                <Route path="courses" element={<RoleBasedRoute requiredRole="courses"><CourseManagement /></RoleBasedRoute>} />
                <Route path="quizzes" element={<RoleBasedRoute requiredRole="quizzes"><QuizManagement /></RoleBasedRoute>} />
                <Route path="cognition-lab" element={<RoleBasedRoute requiredRole="cognition-lab"><CognitionLabManagement /></RoleBasedRoute>} />
                <Route path="blog" element={<RoleBasedRoute requiredRole="blog"><BlogManagement /></RoleBasedRoute>} />
                <Route path="research" element={<RoleBasedRoute requiredRole="research"><ResearchManagement /></RoleBasedRoute>} />
                <Route path="career" element={<RoleBasedRoute requiredRole="career"><CareerManagement /></RoleBasedRoute>} />
                <Route path="certificates" element={<RoleBasedRoute requiredRole="certificates"><CertificatesManagement /></RoleBasedRoute>} />
                <Route path="scholarships" element={<RoleBasedRoute requiredRole="scholarships"><ScholarshipManagement /></RoleBasedRoute>} />
                <Route path="scholarship-applications" element={<RoleBasedRoute requiredRole="scholarships"><ScholarshipApplicationsManagement /></RoleBasedRoute>} />
                <Route path="contact" element={<RoleBasedRoute requiredRole="contact"><ContactManagement /></RoleBasedRoute>} />
                <Route path="donations" element={<RoleBasedRoute requiredRole="donations"><DonationManagement /></RoleBasedRoute>} />
                <Route path="announcements" element={<RoleBasedRoute requiredRole="announcements"><AnnouncementsManager /></RoleBasedRoute>} />
                <Route path="notifications" element={<RoleBasedRoute requiredRole="notifications"><NotificationManagement /></RoleBasedRoute>} />
                <Route path="analytics" element={<RoleBasedRoute requiredRole="analytics"><Analytics /></RoleBasedRoute>} />
                <Route path="events" element={<RoleBasedRoute requiredRole="events"><EventManagement /></RoleBasedRoute>} />
                <Route path="reports" element={<RoleBasedRoute requiredRole="reports"><Reports /></RoleBasedRoute>} />
                <Route path="scholarship-reviews/:applicationId" element={<RoleBasedRoute requiredRole="scholarships"><ScholarshipReviews /></RoleBasedRoute>} />
                <Route path="research-support" element={<RoleBasedRoute requiredRole="research-support"><ResearchSupportManagement /></RoleBasedRoute>} />
              </Route>

              {/* Cognition Lab Routes */}
              <Route path="/cognition-lab" element={<CognitionLabDashboard />} />
              <Route path="/cognition-lab/speed-reading" element={<SpeedReadingTest />} />
              <Route path="/cognition-lab/typing-speed" element={<TypingSpeedTest />} />
              <Route path="/cognition-lab/typing-accuracy" element={<TypingAccuracyDrill />} />
              <Route path="/cognition-lab/memory-recall" element={<MemoryRecallTest />} />
              <Route path="/cognition-lab/flash-comprehension" element={<FlashComprehensionCheck />} />
              <Route path="/cognition-lab/certificates" element={
                <Suspense fallback={<LoadingIndicator />}>
                  <CognitionLabCertificates />
                </Suspense>
              } />
              <Route path="/cognition-lab/certificate/:testType" element={<CognitionLabCertificate />} />

              {/* Pattern Recognition Routes */}
              <Route path="/cognition-lab/pattern-recognition/:testId/setup" element={<PatternTestSetup />} />
              <Route path="/cognition-lab/pattern-recognition/matrices" element={<MatrixPatternTest />} />
              <Route path="/cognition-lab/pattern-recognition/rotation" element={<ShapeRotationTest />} />
              <Route path="/cognition-lab/pattern-recognition/sequences" element={<SequencePatternTest />} />
              <Route path="/cognition-lab/pattern-recognition/mechanical" element={<MechanicalReasoningTest />} />
              <Route path="/cognition-lab/pattern-recognition/clock" element={<ClockPuzzleTest />} />

              {/* 404 Route */}
              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
          </main>
          {showFooter && <Footer />}
          <CookieConsent />
        </div>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;